import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useAreasQuery, useMeQuery } from '@platform/state/api';
import { useWorkspaceMembers } from '../../fields/options/useWorkspaceMembers';
import type { IRecordTypeDefinition } from '@platform/core/types';
import { fieldOptions } from './fieldOptions';
import { dueBuckets, quickFieldsOf, UNASSIGNED } from './quickFieldFilters';
import { treeOptions } from './treeOptions';

/**
 * Варіанти швидкого поля (без «будь-якого»): напрям — дерево напрямів простору з відступами (значення — піддерево); виконавець — «Ім'я (ви)»,
 * інші учасники простору, «Не призначено»; «призначив» — учасники; термін — кошики (прострочено, сьогодні, 7 днів, пізніше, без терміну);
 * пріоритет — варіанти поля з підписами модуля.
 */
export function useQuickFieldOptions(ws: string, rt: IRecordTypeDefinition): (field: string) => { value: string; label: string }[] {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const fields = quickFieldsOf(rt);
  const members = useWorkspaceMembers(ws, !fields.includes('assignee'));
  const { data: areas } = useAreasQuery(ws, { skip: !fields.includes('area') });
  const { data: me } = useMeQuery();
  return useCallback((field: string) => {
    const you = me ? [{ value: 'me', label: t('quickFields.you', { name: me.displayName }) }] : [{ value: 'me', label: t('quickFields.me') }];
    const others = members.filter((m) => m.user.id !== me?.id).map((m) => ({ value: m.user.id, label: m.user.name }));
    if (field === 'assignee') return [...you, ...others, { value: UNASSIGNED, label: t('quickFields.unassigned') }];
    if (field === 'assignedBy') return [...you, ...others];
    if (field === 'area') return treeOptions(areas ?? []);
    if (field === 'due') return dueBuckets.map((b) => ({ value: b, label: t(`quickFields.due.${b}`) }));
    return fieldOptions(rt, rt.fields.find((f) => f.key === field)).map((o) => ({ value: o.value, label: strings.option(rt.key, field, o.value, o.label) }));
  }, [members, me, areas, rt, strings, t]);
}
